import React, { ReactNode, useCallback, useState } from 'react';
import { ControlLink, Drawer, Stack } from '@moda/om';
import ChevronLeftIcon from '@moda/icons/chevron-left-12';
import ChevronRightIcon from '@moda/icons/chevron-right-12';
import { ApolloError } from '@apollo/client';
import { SortOption } from '../../constants';
import { SidebarLayout, Sidebar, Content } from '../SidebarLayout';
import { PageFilter, PageFilterClear } from '../PageFilter';
import { GridItemCount } from '../GridPeripherals/GridItemCount';
import { GridSortByDropdown } from '../GridPeripherals/GridSortByDropdown';
import { SalePercentageTags } from '../SalePercentageTags/SalePercentageTags';
import { PLPBreadcrumbs } from '../PLPBreadcrumbs';
import { GuidePageQuery, ProductListPageResultFragment } from '../../generated/types';

import './ProductListingDesktop.scss';

type DisplayPage = NonNullable<GuidePageQuery['displayPage']>;

type Props = {
  currentSort: SortOption;
  onSortChanged: (sort: string) => void;
  availableSorts: SortOption[];
  headerText?: string;
  error?: ApolloError;
  description?: string;
  showPLPBreadcrumbs?: boolean;
  preserveBreadcrumbSpace?: boolean;
  hideControls?: boolean;
  displayPage?: DisplayPage | null;
  queryResult?: ProductListPageResultFragment | null;
  belowHeader?: ReactNode;
  belowLayout?: ReactNode;
  biggerImagesOnPLP?: boolean;
  children?: React.ReactNode;
};

export const ProductListingDesktop: React.FC<Props> = ({
  children,
  headerText,
  currentSort,
  queryResult,
  displayPage,
  onSortChanged,
  availableSorts,
  showPLPBreadcrumbs,
  preserveBreadcrumbSpace,
  hideControls,
  belowHeader,
  belowLayout,
  biggerImagesOnPLP
}) => {
  const totalItems = queryResult?.totalItems || 0;
  const [filterOpen, setFilterOpen] = useState(false);
  const toggleFilter = useCallback(() => setFilterOpen(open => !open), []);
  const closeFilter = useCallback(() => setFilterOpen(false), []);

  const filterFacets = (
    <PageFilter
      attributes={queryResult?.attributes || []}
      availability={queryResult?.availability || null}
      categoryTree={queryResult?.categoryTree || null}
      colors={queryResult?.filterColors || []}
      designers={queryResult?.designers || []}
      saleTags={queryResult?.saleTags || []}
      sizes={queryResult?.sizes || []}
      guideSubPages={displayPage?.guideSubPages || null}
      onClose={closeFilter}
    />
  );

  return (
    <>
      {showPLPBreadcrumbs ? (
        <PLPBreadcrumbs />
      ) : preserveBreadcrumbSpace ? (
        <div className="ProductListingDesktop__breadcrumbs-placeholder" aria-hidden>
          <PLPBreadcrumbs />
        </div>
      ) : null}

      <h1 className="ProductListingDesktop__header" data-testid="ProductListingDesktop__header">
        {headerText}
      </h1>

      {belowHeader}

      <SalePercentageTags markdownPercentages={queryResult?.markdownPercentages || []} />

      {!hideControls && (
        <Stack
          space={4}
          direction="horizontal"
          justifyContent={biggerImagesOnPLP ? 'space-between' : 'flex-end'}
          alignItems="center"
          className="ProductListingDesktop__grid-container"
        >
          {biggerImagesOnPLP && (
            <ControlLink
              className="ProductListingDesktop__filterToggle"
              underlined={false}
              aria-expanded={filterOpen}
              onClick={toggleFilter}
            >
              {filterOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}
              Filter
            </ControlLink>
          )}

          <Stack space={4} direction="horizontal" alignItems="center">
            <GridItemCount count={totalItems} />

            <GridSortByDropdown
              currentSort={currentSort}
              availableSorts={availableSorts}
              onSortByChange={option => onSortChanged(option)}
            />
          </Stack>
        </Stack>
      )}

      <SidebarLayout>
        {!hideControls &&
          (biggerImagesOnPLP ? (
            <Drawer open={filterOpen}>{filterFacets}</Drawer>
          ) : (
            <Sidebar stickAt={150}>{filterFacets}</Sidebar>
          ))}

        <Content>
          <Stack space={3}>
            <PageFilterClear
              attributes={queryResult?.attributes || []}
              designers={queryResult?.designers || []}
              sizes={queryResult?.sizes || []}
              categoryTree={queryResult?.categoryTree || null}
              showCategoryFilters={biggerImagesOnPLP}
            />
            {children}
          </Stack>
        </Content>
      </SidebarLayout>

      {belowLayout}
    </>
  );
};
